



























import {
  defineComponent,
  computed,
  PropType,
  onMounted,
  toRefs,
  watch
} from '~/utils/nuxt3-migration'
import { MobileMenuItem } from '~/models/header/mobile-menu'
import { useRouter } from '@nuxtjs/composition-api'
import { useI18n } from '~/compositions/i18n'
import { AccessToggleName } from '~/models/user/access-toggles'
import { ciMagnifyingGlass } from '~/icons/source/thin/magnifying-glass'

import { quickSearchCategories } from '~/constants/classified/quick-search'
import { categoriesIconsMappings } from '~/constants/category/icon'
import { useNamespacedStore } from '~/compositions/store'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import MobileMenuEntry from '~/components/car/header/MobileMenuEntry.vue'
import { CategoryId } from '~/models/category/types'
import QuickSearchFetchableCategoryMenu from '~/components/car/classifieds/quick-search/QuickSearchFetchableCategoryMenu.vue'

export default defineComponent({
  components: { QuickSearchFetchableCategoryMenu, MobileMenuEntry },
  props: {
    selectedMenus: {
      type: Array as PropType<string[]>,
      required: true
    },
    clickFunction: {
      type: Function,
      required: true
    },
    navigateFunction: {
      type: Function,
      required: false,
      default: null
    },
    topOffset: {
      type: Number,
      default: 0
    },
    transitioning: {
      type: Boolean,
      default: false
    },
    paddingClass: {
      type: String,
      default: undefined
    },
    forceParent: {
      type: Number as PropType<CategoryId>,
      required: false,
      default: undefined
    }
  },
  setup(props) {
    const router = useRouter()
    const { t } = useI18n()

    const { forceParent } = toRefs(props)

    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)
    const userHasAccessTo = userGetters('userHasAccessTo')

    const categoryMenu = computed<MobileMenuItem[]>(() => {
      function formatCategory(node, parentPath = []): MobileMenuItem {
        if (
          node.id === CategoryId.WANTED_PARTS &&
          !userHasAccessTo(AccessToggleName.WANTED_PARTS_ACCESS)
        ) {
          return null
        }

        const currentPath = [...parentPath, node.name]
        const key = currentPath.join('-')

        const result = {
          key,
          title: t(node.nameTranslationKey),
          id: node.id,
          icon: categoriesIconsMappings[node.id] || null
        }

        if (node.link) {
          if (node.link.name) {
            result.link = router.resolve(node.link).href
            result.useNuxtLink = true
          } else if (node.link.path) {
            result.link = node.link.path
            result.useNuxtLink = true
          }
        }

        if (node.children && node.children.length) {
          const childrenFormatted = node.children
            .map(child => formatCategory(child, currentPath))
            .filter(Boolean)
          if (childrenFormatted.length) {
            result.subLinks = childrenFormatted
          }
        } else if (
          node.facets?.some(
            f =>
              f.name === 'category' &&
              f.type === 'FrontEndCategoryGridIconsHandler'
          )
        ) {
          result.hasFetchableSubLinks = true
        }
        return result
      }

      const categoryEntries = Object.values(quickSearchCategories)
        .map(topLevelNode => formatCategory(topLevelNode))
        .filter(Boolean)

      return [
        ...categoryEntries,
        {
          title: t('search classified with id'),
          icon: ciMagnifyingGlass,
          link: router.resolve({ name: '__search_classified_by_id' }).href,
          useNuxtLink: true
        }
      ]
    })

    function handleForceParent() {
      if ([CategoryId.JOBS, CategoryId.VEHICLES].includes(forceParent.value)) {
        props.clickFunction({ target: { value: null } }, null, true)
      } else if (forceParent.value && props.clickFunction) {
        const foundMenu = categoryMenu.value.find(
          node => node.subLinks?.length && node.id === forceParent.value
        )
        if (foundMenu) {
          // mock the checkbox being clicked
          props.clickFunction(
            { target: { value: `sub-${foundMenu.key}` } },
            foundMenu.title,
            true
          )
        }
      }
    }

    onMounted(() => {
      handleForceParent()
    })
    watch(forceParent, (val, oldVal) => {
      if (val !== oldVal) {
        handleForceParent()
      }
    })

    return { categoryMenu }
  }
})
